import React, { useState } from 'react';
import { Eye, Clock, CheckCircle2, AlertCircle, X, ChevronLeft, ChevronRight, Image, Loader2, XCircle, CheckCircle, ThumbsUp, ShieldCheck } from 'lucide-react';
import FilterTemplate from '../components/filtertemplate/FilterTemplate';
import HelpButton from '../components/shared/HelpButton';
import CustomSelect from '../components/shared/CustomSelect';
import { fetchClientTickets, approveClientTicket } from '../utils/api';
import { formatDateForDisplay } from '../utils/format';
import { useUser } from '../utils/UserContext';
import { rolesAccess } from '../data/map';
import { getBrandOrDefault } from '../data/map';
import { cssUrl } from '../utils/cssVersion';


const STATUS = { PENDING: 'Pending', IN_PROGRESS: 'In Progress', COMPLETED: 'Ticket Completed' };
const APPROVAL = { APPROVED: 'Approved', PENDING: 'Pending Approval' };

const JOB_TYPE_OPTIONS = [
  'Cancel Shift', 'Additional Clean', 'Builders Clean', 'Detail Clean', 'Request Audit',
  'Request Site Inspection', 'Periodical Carpark Clean', 'Periodical Carpet Clean',
  'External Service', 'Feed back', 'Fridge Clean', 'Hard Floor Clean', 'Rectification Clean',
  'Specialist Clean Outside Of Scope', 'Steam Clean Carpet', 'Window Clean', 'Periodical', 'Order Request',
];
const TIME_OPTIONS = ['7am', '8am', '9am', '10am', '11am', '12pm', '1pm', '2pm', '3pm', '4pm', '5pm', '6pm', '7pm', '8pm', '9pm'];
const APPROVAL_OPTIONS = [APPROVAL.APPROVED, APPROVAL.PENDING];

const hexToRgb = (hex) => {
  const h = hex.replace('#', '');
  return `${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)}`;
};

const makeTheme = (accent) => ({
  accent,
  rgb: hexToRgb(accent),
  gradientText: {
    background: `linear-gradient(135deg, ${accent} 0%, #60a5fa 50%, #8b5cf6 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    filter: `drop-shadow(0 0 20px rgba(${hexToRgb(accent)},0.3))`,
  },
});

const statusMeta = (status) => {
  switch (status) {
    case STATUS.COMPLETED:   return { color: '#22c55e', bg: 'rgba(34,197,94,0.1)',   icon: <CheckCircle2 size={13} /> };
    case STATUS.IN_PROGRESS: return { color: '#fbbf24', bg: 'rgba(251,191,36,0.1)',  icon: <Clock size={13} /> };
    default:                 return { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', icon: <AlertCircle size={13} /> };
  }
};

const approvalMeta = (approval) =>
  approval === APPROVAL.APPROVED
    ? { color: '#22c55e', bg: 'rgba(34,197,94,0.1)',  icon: <ShieldCheck size={13} /> }
    : { color: '#fbbf24', bg: 'rgba(251,191,36,0.1)', icon: <Clock size={13} /> };

const parseImages = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(i => (typeof i === 'object' ? i.url : i)).filter(Boolean);
  if (typeof raw === 'string') return raw.split(',').map(u => u.trim()).filter(Boolean);
  return [];
};

const getLastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();

// ─────────────────────────────────────────────
// Ticket Card — approval-first
// ─────────────────────────────────────────────
const TicketCard = ({ item, onClick, theme }) => {
  const approval = item[6] || APPROVAL.PENDING;
  const status = item[7] || STATUS.PENDING;
  const aMeta = approvalMeta(approval);
  const sMeta = statusMeta(status);
  const needsApproval = approval !== APPROVAL.APPROVED;

  return (
    <div
      onClick={() => onClick(item)}
      className="relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 hover:scale-[1.01] hover:shadow-xl"
      style={{
        background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(9,18,32,0.98) 100%)',
        border: `1px solid ${aMeta.color}40`,
        boxShadow: `0 4px 24px ${aMeta.color}15`,
      }}
    >
      <div className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, transparent, ${aMeta.color}, transparent)` }} />

      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 uppercase tracking-widest mb-0.5">{item[2] || 'No Type'}</p>
            <h3 className="text-white font-semibold text-sm truncate">{item[9] || 'No Subject'}</h3>
            <p className="text-slate-400 text-xs mt-0.5 truncate">Requested by {item[8] || '—'}</p>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0"
            style={{ background: aMeta.bg, color: aMeta.color, border: `1px solid ${aMeta.color}50` }}>
            {aMeta.icon}{approval}
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Created: {formatDateForDisplay(item[3]) || '—'}</span>
          {item[4] && <span>Due: {formatDateForDisplay(item[4])}{item[5] ? ` · ${item[5]}` : ''}</span>}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs"
            style={{ background: sMeta.bg, color: sMeta.color, border: `1px solid ${sMeta.color}40` }}>
            {sMeta.icon}<span>{status}</span>
          </div>
          <div className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
            style={{
              background: needsApproval ? `rgba(${theme.rgb},0.12)` : 'rgba(148,163,184,0.08)',
              color: needsApproval ? theme.accent : '#94a3b8',
              border: `1px solid ${needsApproval ? theme.accent + '40' : '#94a3b830'}`,
            }}>
            {needsApproval ? <ThumbsUp size={11} /> : <Eye size={11} />}
            <span>{needsApproval ? 'Approve' : 'View'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Image Carousel
// ─────────────────────────────────────────────
const ImageCarousel = ({ images, title, theme }) => {
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const safe = Math.min(idx, Math.max(images.length - 1, 0));
  const next = () => setIdx(p => (p === images.length - 1 ? 0 : p + 1));
  const prev = () => setIdx(p => (p === 0 ? images.length - 1 : p - 1));

  if (!images || images.length === 0) {
    return (
      <div>
        <label className="block text-sm font-medium mb-2" style={theme.gradientText}>{title}</label>
        <div className="rounded-2xl flex items-center justify-center h-48 bg-gray-800/50" style={{ border: `1px solid ${theme.accent}20` }}>
          <div className="text-center">
            <Image size={28} className="mx-auto text-gray-600 mb-2" />
            <p className="text-gray-500 text-sm">No images available</p>
          </div>
        </div>
      </div>
    );
  }

  const navBtn = (onClick, side, Icon) => (
    <button onClick={onClick}
      className={`absolute ${side}-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300`}
      style={{ width: 40, height: 40, background: 'rgba(17,24,39,0.9)', border: `1px solid ${theme.accent}`, borderRadius: '50%' }}>
      <Icon size={20} className="mx-auto" style={{ color: theme.accent }} />
    </button>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium" style={theme.gradientText}>{title}</label>
        {images.length > 1 && (
          <span className="px-2 py-0.5 rounded-full text-white text-xs font-medium"
            style={{ background: `linear-gradient(135deg,${theme.accent},#60a5fa)`, boxShadow: `0 0 10px rgba(${theme.rgb},0.3)` }}>
            {safe + 1} / {images.length}
          </span>
        )}
      </div>

      <div className="relative overflow-hidden rounded-2xl group"
        style={{ border: `1px solid ${theme.accent}`, boxShadow: `0 8px 32px rgba(${theme.rgb},0.1)` }}
        onTouchStart={e => setTouchStart(e.targetTouches[0].clientX)}
        onTouchMove={e => setTouchEnd(e.targetTouches[0].clientX)}
        onTouchEnd={() => { const d = touchStart - touchEnd; if (d > 50) next(); if (d < -50) prev(); }}>
        <div className="h-72 flex items-center justify-center bg-gray-900/50">
          <img src={images[safe]} alt={`${title} ${safe + 1}`}
            className={`max-w-full max-h-full object-contain transition-all duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setLoaded(true)}
            onError={e => { e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iIzFmMjkzNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM2Yjc1ODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBVbmF2YWlsYWJsZTwvdGV4dD48L3N2Zz4='; }} />
        </div>
        {images.length > 1 && (<>{navBtn(prev, 'left', ChevronLeft)}{navBtn(next, 'right', ChevronRight)}</>)}
      </div>

      {images.length > 1 && (
        <div className="flex justify-center mt-3 gap-2">
          {images.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} className="transition-all duration-300"
              style={{ width: i === safe ? 24 : 8, height: 8, borderRadius: 4,
                background: i === safe ? `linear-gradient(135deg,${theme.accent},#60a5fa)` : '#4b5563',
                boxShadow: i === safe ? `0 0 8px rgba(${theme.rgb},0.5)` : 'none' }} />
          ))}
        </div>
      )}
      <p className="text-center text-xs text-gray-500 mt-2">Swipe or use arrows to navigate</p>
    </div>
  );
};

// ─────────────────────────────────────────────
// Modal shell shared by View + Approve
// ─────────────────────────────────────────────
const ModalShell = ({ title, icon, onClose, theme, children, footer }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" style={{ top: 'var(--fr-modal-top, 0px)', paddingBottom: 'calc(84px + env(safe-area-inset-bottom, 0px))' }}>
    <div className="bg-gray-900/95 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" style={{ border: `1px solid ${theme.accent}`, maxHeight: 'min(90vh, calc(100dvh - var(--fr-modal-top, 0px) - 100px))' }}>
      <div className="p-6 border-b border-gray-800 flex justify-between items-center flex-shrink-0">
        <h2 className="text-2xl font-bold flex items-center gap-2" style={theme.gradientText}>{icon}{title}</h2>
        <button onClick={onClose} className="text-gray-400 transition-colors" style={{ '--hover': theme.accent }}
          onMouseEnter={e => (e.currentTarget.style.color = theme.accent)} onMouseLeave={e => (e.currentTarget.style.color = '')}>
          <X size={24} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto"><div className="p-6 space-y-4">{children}</div></div>
      <div className="p-6 border-t border-gray-800 flex justify-end gap-4 flex-shrink-0">{footer}</div>
    </div>
  </div>
);

const ReadField = ({ label, value, theme, rows }) => (
  <div>
    <label className="block text-sm font-medium mb-1" style={theme.gradientText}>{label}</label>
    {rows
      ? <textarea value={value || ''} readOnly rows={rows} className="w-full p-2 rounded-lg bg-gray-800/60 text-white border border-gray-700 focus:outline-none resize-none" />
      : <input type="text" value={value || ''} readOnly className="w-full p-2 rounded-lg bg-gray-800/60 text-white border border-gray-700 focus:outline-none cursor-not-allowed" />}
  </div>
);

const btnStyle = (theme, glow) => ({
  border: `1px solid ${theme.accent}`,
  boxShadow: glow ? `0 0 20px rgba(${theme.rgb},0.2)` : 'none',
});

// ─────────────────────────────────────────────
// View Modal (already approved)
// ─────────────────────────────────────────────
const ViewClientTicketModal = ({ item, onClose, theme }) => (
  <ModalShell title="Ticket Details" icon={<Eye size={22} />} onClose={onClose} theme={theme}
    footer={
      <button onClick={onClose}
        className="px-6 py-2 rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-black text-white font-medium hover:shadow-lg flex items-center gap-2 transition-all"
        style={btnStyle(theme, true)}>
        <X size={16} /> Close
      </button>
    }>
    <ReadField label="Job Request Type" value={item[2]} theme={theme} />
    <ReadField label="Subject" value={item[9]} theme={theme} />
    <ReadField label="Job Request Details" value={item[10]} theme={theme} rows={3} />
    <div className="grid grid-cols-2 gap-4">
      <ReadField label="Created" value={formatDateForDisplay(item[3])} theme={theme} />
      <ReadField label="Completed" value={item[4] ? `${formatDateForDisplay(item[4])}${item[5] ? ` · ${item[5]}` : ''}` : ''} theme={theme} />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium mb-1" style={theme.gradientText}>Client Approval</label>
        <div className="w-full p-2 rounded-lg border text-white font-medium"
          style={{ background: approvalMeta(item[6]).bg, borderColor: approvalMeta(item[6]).color }}>{item[6] || ''}</div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1" style={theme.gradientText}>Status</label>
        <div className="w-full p-2 rounded-lg border text-white font-medium"
          style={{ background: statusMeta(item[7]).bg, borderColor: statusMeta(item[7]).color }}>{item[7] || ''}</div>
      </div>
    </div>
    <ReadField label="Cleaner" value={item[11] || 'Not assigned'} theme={theme} />
    <ImageCarousel images={parseImages(item[13])} title="Before Photos" theme={theme} />
    <ReadField label="Cleaner Comments" value={item[15] || 'No Comments'} theme={theme} rows={3} />
    <ImageCarousel images={parseImages(item[14])} title="Finished Photos" theme={theme} />
  </ModalShell>
);

// ─────────────────────────────────────────────
// Approve Modal (Pending Approval) — the seven update_single_ticket fields
// ─────────────────────────────────────────────
const ApproveTicketModal = ({ item, onClose, onSubmit, isSubmitting, setToast, theme }) => {
  const createdDate = item[3] || '';
  const [form, setForm] = useState({
    requestType:    item[2] || '',
    dateComplete:   item[4] || '',
    completeTime:   item[5] || '',
    clientApproval: item[6] || APPROVAL.PENDING,
    subjectTitle:   item[9] || '',
    jobDetails:     item[10] || '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Same anchor rules as FilterTickets: not before created, same month/year (clamped)
  const handleCompletedDateChange = (val) => {
    if (!val || !createdDate) { set('dateComplete', val); return; }
    if (val < createdDate) {
      setToast?.({ message: 'Completed date cannot be before the created date.', type: 'warning' });
      return;
    }
    const [cy, cm] = createdDate.split('-').map(Number);
    const [vy, vm] = val.split('-').map(Number);
    if (cy !== vy || cm !== vm) {
      const lastDay = getLastDayOfMonth(cy, cm - 1);
      const pad = (n) => String(n).padStart(2, '0');
      setToast?.({ message: `Completed date must be in the same month as the created date. Clamped to ${lastDay}th.`, type: 'warning' });
      set('dateComplete', `${cy}-${pad(cm)}-${pad(lastDay)}`);
      return;
    }
    set('dateComplete', val);
  };

  const validate = () => {
    const missing = [];
    if (!form.requestType)    missing.push('Job Request Type');
    if (!form.dateComplete)   missing.push('Completed Date');
    if (!form.completeTime)   missing.push('Completed Time');
    if (!form.clientApproval) missing.push('Client Approval');
    if (!form.subjectTitle.trim()) missing.push('Subject');
    if (!form.jobDetails.trim())   missing.push('Job Request Details');
    if (missing.length) { setToast?.({ message: `Required: ${missing.join(', ')}`, type: 'warning' }); return false; }
    if (form.dateComplete && createdDate && form.dateComplete < createdDate) {
      setToast?.({ message: 'Completed date cannot be before the created date.', type: 'warning' }); return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const fd = new FormData();
    fd.append('recordids',      item[0]);
    fd.append('requesttype',    form.requestType);
    fd.append('datecomplete',   form.dateComplete);
    fd.append('completetime',   form.completeTime);
    fd.append('clientapproval', form.clientApproval);
    fd.append('subjecttitle',   form.subjectTitle.trim());
    fd.append('jobdetails',     form.jobDetails.trim());
    onSubmit(fd);
  };

  const inputClass = 'w-full p-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none';

  return (
    <ModalShell title="Approve Ticket" icon={<ThumbsUp size={22} />} onClose={onClose} theme={theme}
      footer={<>
        <button onClick={onClose}
          className="px-6 py-2 rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-black text-white font-medium hover:shadow-lg flex items-center gap-2 transition-all"
          style={btnStyle(theme, false)}>
          <XCircle size={16} /> Cancel
        </button>
        <button onClick={handleSubmit} disabled={isSubmitting}
          className="px-6 py-2 rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-black text-white font-medium hover:shadow-lg flex items-center gap-2 transition-all disabled:opacity-50"
          style={btnStyle(theme, true)}>
          {isSubmitting
            ? <><Loader2 size={16} className="animate-spin" /> Submitting…</>
            : <><CheckCircle size={16} /> Submit Approval</>}
        </button>
      </>}>
      <ReadField label="Client" value={(item[1] || '').toUpperCase()} theme={theme} />
      <ReadField label="Requested By" value={item[8]} theme={theme} />

      <div>
        <label className="block text-sm font-medium mb-1" style={theme.gradientText}>Job Request Type</label>
        <CustomSelect label="" value={form.requestType} onChange={(v) => set('requestType', v)} options={JOB_TYPE_OPTIONS} />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1" style={theme.gradientText}>Subject</label>
        <input type="text" value={form.subjectTitle} onChange={(e) => set('subjectTitle', e.target.value)}
          className={inputClass} style={{ borderColor: form.subjectTitle ? undefined : '#f87171' }} />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1" style={theme.gradientText}>Job Request Details</label>
        <textarea value={form.jobDetails} onChange={(e) => set('jobDetails', e.target.value)} rows="3" className={`${inputClass} resize-none`} />
      </div>

      <ImageCarousel images={parseImages(item[13])} title="Before Photos" theme={theme} />

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1" style={theme.gradientText}>Created Date</label>
          <input type="date" value={createdDate} readOnly className={`${inputClass} bg-gray-800/60 cursor-not-allowed [color-scheme:dark]`} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1" style={theme.gradientText}>Completed Date</label>
          <input type="date" value={form.dateComplete} min={createdDate || undefined}
            onChange={(e) => handleCompletedDateChange(e.target.value)} className={`${inputClass} [color-scheme:dark]`} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1" style={theme.gradientText}>Completed Time</label>
          <CustomSelect label="" value={form.completeTime} onChange={(v) => set('completeTime', v)} options={TIME_OPTIONS} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1" style={theme.gradientText}>Client Approval</label>
          <CustomSelect label="" value={form.clientApproval} onChange={(v) => set('clientApproval', v)} options={APPROVAL_OPTIONS} />
        </div>
      </div>

      {item[15] && <ReadField label="Cleaner Comments" value={item[15]} theme={theme} rows={2} />}
      {parseImages(item[14]).length > 0 && <ImageCarousel images={parseImages(item[14])} title="Finished Photos" theme={theme} />}
    </ModalShell>
  );
};

// ─────────────────────────────────────────────
// Chips — approval first, then status, then today
// ─────────────────────────────────────────────
const ClientChips = (chipQuery, setChipQuery) => {
  const chips = [
    { label: 'Pending Approval', value: 'pending approval', color: '#fbbf24' },
    { label: 'Approved',         value: 'approved',         color: '#22c55e' },
    { label: 'In Progress',      value: 'in progress',      color: '#fbbf24' },
    { label: 'Completed',        value: 'ticket completed', color: '#22c55e' },
    { label: 'Today',            value: 'today',            color: '#60a5fa' },
  ];
  const active = chipQuery ? chipQuery.toLowerCase().split(',').map(t => t.trim()).filter(Boolean) : [];
  const toggle = (val) => setChipQuery((active.includes(val) ? active.filter(t => t !== val) : [...active, val]).join(', '));

  return (
    <div className="px-4 pb-2">
      <div className="flex items-center gap-2 flex-wrap">
        {chips.map(({ label, value, color }) => {
          const on = active.includes(value);
          return (
            <button key={value} onClick={() => toggle(value)}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all duration-200"
              style={{
                background: on ? `${color}22` : 'rgba(255,255,255,0.04)',
                color: on ? color : '#94a3b8',
                border: `1px solid ${on ? color + '60' : color + '90'}`,
                boxShadow: on ? `0 0 10px ${color}30` : 'none',
              }}>
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: on ? color : '#6b7280' }} />
              {label}{on && <X size={10} />}
            </button>
          );
        })}
        {active.length > 0 && (
          <button onClick={() => setChipQuery('')}
            className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-gray-500 hover:text-gray-300 transition-colors"
            style={{ border: '1px solid #374151' }}>
            <X size={10} /> Clear All
          </button>
        )}
      </div>
    </div>
  );
};
const IGLU_HELP_GUIDE = '/static/pdffilters/IgluTicketHelpGuide.pdf';
const CLIENT_HELP_GUIDE = '/static/pdffilters/ClientOverview.pdf';

const FilterClientTickets = () => {
  const { user } = useUser();
  const isIglu = user?.group === 'IGLU South Yarra';
  const helpGuide = isIglu ? IGLU_HELP_GUIDE : CLIENT_HELP_GUIDE;
  const theme = makeTheme(getBrandOrDefault(user).accent);

  const renderModal = (item, onClose, onSubmit, isSubmitting, setToast) =>
    (item[6] === APPROVAL.APPROVED)
      ? <ViewClientTicketModal item={item} onClose={onClose} theme={theme} />
      : <ApproveTicketModal item={item} onClose={onClose} onSubmit={onSubmit} isSubmitting={isSubmitting} setToast={setToast} theme={theme} />;

  return (
    <FilterTemplate
      title="My Tickets"
      helpButton={<HelpButton helpData={[{ chip: 'client_tickets', urlFilter: helpGuide }]} />}
      subtitle="Review & approve your service tickets"
      backRoute="/profile"
      searchPlaceholder="Search your tickets..."
      stylesheetHref={cssUrl('filtertemplate.css')}
      fetchData={fetchClientTickets}
      dataKey="tickets"
      searchFields={[2, 6, 7, 8, 9, 11]}       // 6 = Client Approval so the approval chips match
      chipLogic="or"
      todayFilterIndex={3}
      renderCard={(item, onClick) => <TicketCard item={item} onClick={onClick} theme={theme} />}
      renderModal={renderModal}
      onSubmit={approveClientTicket}
      emptyIcon="🎫"
      emptyTitle="No Tickets Found"
      loadingMessage="Loading your tickets..."
      showNavigation={true}
      showHeader={false}
      renderChips={ClientChips}
    />
  );
};

export default FilterClientTickets;